import React, { useState, useEffect, useRef } from 'react'
import { loadSdkScript } from './utils'
import { useOsSdk } from './useOsSdk'
import {
  LOGIN_SUCCESS,
  LOGIN_FAILED,
  AUTO_LOGIN_SUCCESS,
  AUTO_LOGIN_FAILED,
  SET_PROJECT_IDENTIFIERS_SUCCESS,
  SET_PROJECT_IDENTIFIERS_FAILED,
  PROJECT_CREATE_SUCCESS,
  PROJECT_LOAD_SUCCESS,
  PROJECT_CREATE_OR_LOAD_FAILED,
  SDK_BOOT_FAILED
} from './notifications'

const OsSdkView = ({
  projectData,
  osScriptLocation,
  osSdkKey,
  extraScriptParams,
  sdkConfig,
  osToken,
  projectIdentifiersToken,
  onLoad,
  onRemove,
  onMessage,
  toolbar,
  height
}) => {
  const [isReady, setIsReady] = useState(false)
  const { loaded, ossdk } = useOsSdk()

  const viewRef = useRef(null)
  const ossdkRef = useRef(null)

  const initOssdk = async (ossdk) => {
    const view = ossdk.createView()
    view.spawn(window.document.getElementById('opensolar-studio-1'))
    return view
  }

  const attemptAutoLogin = (ossdk) => {
    return ossdk.auth
      .attemptAutoLogin()
      .then(() => {
        if (onMessage) onMessage(AUTO_LOGIN_SUCCESS)
      })
      .catch((err) => {
        ossdk.route.goto('/login')
        if (onMessage) onMessage(AUTO_LOGIN_FAILED, err)
      })
  }

  const loginWithToken = (ossdk, token) => {
    return ossdk.auth
      .loginWithToken({ token })
      .then(() => {
        if (onMessage) onMessage(LOGIN_SUCCESS)
      })
      .catch((err) => {
        if (onMessage) onMessage(LOGIN_FAILED, err)
      })
  }

  const setProjectIdentifiersToken = (ossdk, token) => {
    return ossdk.auth
      .setProjectIdentifiersToken(token)
      .then(() => {
        if (onMessage) onMessage(SET_PROJECT_IDENTIFIERS_SUCCESS)
      })
      .catch((err) => {
        if (onMessage) onMessage(SET_PROJECT_IDENTIFIERS_FAILED, err)
      })
  }

  const createOrUpdateProject = (ossdk, projectData) => {
    return ossdk.flows
      .createOrUpdateProject({
        identifier: projectData.identifier,
        fields: projectData
      })
      .then((isCreated) => {
        if (onMessage)
          if (isCreated === true) {
            onMessage(PROJECT_CREATE_SUCCESS)
          } else {
            onMessage(PROJECT_LOAD_SUCCESS)
          }
      })
      .catch((err) => {
        if (onMessage) onMessage(PROJECT_CREATE_OR_LOAD_FAILED, err)
      })
  }

  const loadSdkScriptAndInitSdk = async () => {
    let ossdk
    try{
      ossdk = await loadSdkScript(osScriptLocation, osSdkKey, extraScriptParams, sdkConfig)
      ossdkRef.current = ossdk
    } catch(e) {
      if(onMessage) {
        onMessage(SDK_BOOT_FAILED, e)
        return 
      }else{
        throw e
      }
    }

    const view = await initOssdk(ossdk)
    viewRef.current = view

    // Currently if onLoad changes while the above promises are out, stuff will break
    // TODO: fix this
    if (onLoad) {
      onLoad(ossdk)
    }
  }

  useEffect(() => {
    if(!loaded) return
    setProjectIdentifiersToken(ossdk, projectIdentifiersToken)
  }, [loaded, projectIdentifiersToken])


  useEffect(() => {
    if(!loaded) return

    if (isReady && !osToken) {
      // At the moment there's no difference between logging out and auto-logging in, can't really deal with this scenario
      // TODO: fix this but introducing a API for auto-login (e.g. osToken="auto-login")
      return
    }

    // in case token has changed after readiness, go back to unready state
    setIsReady(false)

    let promise
    if (osToken) {
      promise = loginWithToken(ossdk, osToken).then(() => {
        return createOrUpdateProject(ossdk, projectData)
      })
    } else {
      promise = attemptAutoLogin(ossdk).then(() => {
        return createOrUpdateProject(ossdk, projectData)
      })
    }
    promise.then(() => {
      setIsReady(true)
    })
  }, [loaded, osToken])

  useEffect(() => {
    loadSdkScriptAndInitSdk()

    return () => {
      window.hasLoaded = false
      if (onRemove) {
        if (!ossdkRef.current) {
          console.warn('ossdk not found, onRemove called with undefined as argument')
        }
        onRemove(ossdkRef.current)
      }

      // This also runs peerCleanup() and removes the view from sdk.views
      if (ossdkRef.current?.removeView && viewRef.current) {
        ossdkRef.current.removeView(viewRef.current)
      } else {
        console.warn('ossdkRef.current.removeView or viewRef.current not found. View not removed.')
      }
    }
  }, [])

  return (
    <React.Fragment>
      {!!toolbar && toolbar}
      <div
        id='os-views'
        className='main'
        style={{
          backgroundColor: '#fff',
          position: 'relative',
          height: height || 600,
          width: '100%',
          margin: 'auto'
        }}
      >
        <div
          id='opensolar-studio-1'
          className='os-view'
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0
          }}
        />
      </div>
    </React.Fragment>
  )
}

export default OsSdkView
